import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import cn from 'classnames';

import ToolCallCard, { getToolCallLabel } from './ToolCallCard';
import { getPrettyDurationPrecise } from './helpers/format';

/*
  A run of consecutive calls to the same tool (e.g. four "Discover creators") collapsed into one
  concise card with a count, so a repeated step doesn't sprawl the stream. Expanding reveals each
  call as its own ToolCallCard — full timing, inputs, and outputs per run.

  The summary and the expanded members render as SIBLINGS (a Fragment), not nested: the existing
  `.ap-tool-call.is-running/.is-done` rules use descendant combinators, so nesting the member cards
  inside the group's `.ap-tool-call` would leak the group's aggregate status onto every member glyph.
  Keeping members outside the group node lets each member style by its own status.
*/
const ToolCallGroupCard = ({ group, thread, renderResultCard, lifecycleEventsVisible, onReplayLifecycleEvent }) => {
  const [isOpen, setIsOpen] = useState(false);

  const calls = group.calls || [];
  const total = calls.length;
  const settled = calls.filter(c => Array.isArray(c.toolResults) && c.toolResults.length > 0).length;
  const allDone = settled >= total;
  const status = allDone ? 'done' : 'running';
  const label = getToolCallLabel(group.toolName, allDone ? 'done' : 'running');

  // The slowest member headlines the collapsed chip — the number you'd expand to find
  // when a step feels slow.
  const memberDurations = calls.map(c => _.get(c, 'toolResults.0.metadata.durationMs')).filter(Number.isFinite);
  const slowestText = allDone && memberDurations.length ? getPrettyDurationPrecise(Math.max(...memberDurations)) : null;

  return (
    <>
      <div className={cn('ap-tool-call', 'ap-tool-call-group', `is-${status}`, { 'is-open': isOpen })}>
        <button
          type='button'
          className='ap-tool-call-summary'
          onClick={() => setIsOpen(o => !o)}
          aria-expanded={isOpen}
        >
          <span className='ap-tool-call-glyph' aria-hidden='true' />
          <span className='ap-tool-call-label'>
            {label}
            <span className='ap-tool-call-count'>{`\u00D7${total}`}</span>
            {slowestText && <span className='ap-tool-call-elapsed'>{` \u00B7 ${slowestText}`}</span>}
          </span>
          <span className='ap-tool-call-chevron' aria-hidden='true'>{'\u203A'}</span>
        </button>
      </div>
      {isOpen && (
        <div className='ap-tool-call-group-members'>
          {calls.map(call => (
            <ToolCallCard
              key={call.id}
              call={call}
              results={call.toolResults}
              thread={thread}
              renderResultCard={renderResultCard}
              lifecycleEventsVisible={lifecycleEventsVisible}
              onReplayLifecycleEvent={onReplayLifecycleEvent}
            />
          ))}
        </div>
      )}
    </>
  );
};

ToolCallGroupCard.propTypes = {
  group: PropTypes.shape({
    toolName: PropTypes.string,
    calls: PropTypes.array.isRequired
  }).isRequired,
  thread: PropTypes.object,
  renderResultCard: PropTypes.func,
  lifecycleEventsVisible: PropTypes.bool,
  onReplayLifecycleEvent: PropTypes.func
};

export default ToolCallGroupCard;
